import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, SearchIcon, CheckCircleIcon, ClockIcon, XCircleIcon, MoreHorizontalIcon, EyeIcon, EditIcon, ArchiveIcon, MailIcon, DownloadIcon, CalendarIcon, UserIcon, ChevronRightIcon, FilterIcon, SlidersIcon, StoreIcon, ChevronDownIcon } from 'lucide-react';
// Order status types
export type OrderStatus = 'new' | 'in-progress' | 'open' | 'completed' | 'cancelled';
// Order type definition
export interface Order {
  id: string;
  status: OrderStatus;
  paymentStatus: 'paid' | 'unpaid' | 'cancelled';
  customer: {
    name: string;
    email: string;
    address: string;
    mobileNumber: string;
    additionalPhone?: string;
    address2?: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  date: string;
  time: string;
  transaction?: string;
  transactionStatus?: 'confirmed' | 'pending' | 'failed';
  notes?: string;
  shopId: string;
  shopName: string;
}
// Shop list for filtering
const shopsList = [{
  id: '1',
  name: 'Beach Shop'
}, {
  id: '2',
  name: 'Island Shop'
}, {
  id: '3',
  name: 'ABC'
}];
// Mock order data
const mockOrders: Order[] = [{
  id: '220629-1e753',
  status: 'new',
  paymentStatus: 'paid',
  customer: {
    name: 'randy',
    email: 'randy@randy.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '6596605522',
    additionalPhone: '1234',
    address2: '1234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '29/06/2022',
  time: '14:28',
  transaction: '62bc1b4be3c2250009f1e753',
  transactionStatus: 'confirmed',
  shopId: '1',
  shopName: 'Beach Shop'
}, {
  id: '210505-00003',
  status: 'new',
  paymentStatus: 'cancelled',
  customer: {
    name: 'SOWMIYA',
    email: 'sowme76@gmail.com',
    address: 'ADDESS1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 3,
    price: 110.0
  }],
  total: 330.0,
  date: '05/05/2021',
  time: '07:52',
  shopId: '2',
  shopName: 'Island Shop'
}, {
  id: '210506-00004',
  status: 'in-progress',
  paymentStatus: 'paid',
  customer: {
    name: 'sowmiya',
    email: 'sowme76@gmail.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '06/05/2021',
  time: '12:48',
  shopId: '1',
  shopName: 'Beach Shop'
}, {
  id: '210504-00002',
  status: 'in-progress',
  paymentStatus: 'cancelled',
  customer: {
    name: 'sowmiya',
    email: 'sowmiya@pomel.com',
    address: 'Address Line 1',
    mobileNumber: '6598661234'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '04/05/2021',
  time: '16:06',
  shopId: '3',
  shopName: 'ABC'
}, {
  id: '210503-00001',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'John Doe',
    email: 'john@example.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '9876543210'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '03/05/2021',
  time: '10:30',
  shopId: '2',
  shopName: 'Island Shop'
}, {
  id: '210502-00005',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Jane Smith',
    email: 'jane@example.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '9876543211'
  },
  items: [{
    name: 'Kittens',
    quantity: 1,
    price: 111.0
  }],
  total: 111.0,
  date: '02/05/2021',
  time: '11:15',
  shopId: '1',
  shopName: 'Beach Shop'
}, {
  id: '210501-00006',
  status: 'completed',
  paymentStatus: 'paid',
  customer: {
    name: 'Alex Brown',
    email: 'alex@example.com',
    address: 'Kuala Lumpur, Malaysia',
    mobileNumber: '9876543212'
  },
  items: [{
    name: 'Kittens',
    quantity: 2,
    price: 111.0
  }],
  total: 222.0,
  date: '01/05/2021',
  time: '09:45',
  shopId: '3',
  shopName: 'ABC'
}];
export function Orders() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('all');
  const [shopFilter, setShopFilter] = useState<string>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-amber-100 text-amber-800',
      open: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-12020">
        {labels[status]}
      </span>;
  };
  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon size={14} className="text-green-500" data-id="element-12021" />;
      case 'unpaid':
        return <ClockIcon size={14} className="text-amber-500" data-id="element-12022" />;
      case 'cancelled':
        return <XCircleIcon size={14} className="text-red-500" data-id="element-12023" />;
      default:
        return null;
    }
  };
  const filteredOrders = mockOrders.filter(order => {
    const matchesSearch = searchQuery === '' || order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = activeTab === 'all' || order.status === activeTab;
    const matchesShop = shopFilter === 'all' || order.shopId === shopFilter;
    return matchesSearch && matchesStatus && matchesShop;
  });
  const totalAmount = filteredOrders.reduce((sum, order) => sum + order.total, 0);
  const completedAmount = filteredOrders.filter(order => order.status === 'completed').reduce((sum, order) => sum + order.total, 0);
  const cancelledAmount = filteredOrders.filter(order => order.status === 'cancelled').reduce((sum, order) => sum + order.total, 0);
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-12024">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-12025">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12026">
          {t('pages.Orders.orders')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-12027">
          <button onClick={() => window.location.href = '/settings?tab=order-settings'} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12028">
            {t('pages.Orders.settings')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12029">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12030">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12031">
            <ShoppingCartIcon size={16} className="text-gray-500" data-id="element-12032" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12033">
              {t('pages.Orders.total_orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12034">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12035">
              {filteredOrders.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12036">
            {shopFilter === 'all' ? 'All shops' : shopsList.find(s => s.id === shopFilter)?.name}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12037">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12038">
            <span className="text-gray-500 font-medium" data-id="element-12039">$</span>
            <span className="text-sm font-semibold text-gray-900" data-id="element-12040">
              {t('pages.Orders.total_amount')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12041">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12042">
              $
              {totalAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12043">
            {t('pages.Orders.across')} {filteredOrders.length}{' '}
            {t('components.ShopDashboard.orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12044">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12045">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-12046" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12047">
              {t('components.getOrderStatusBadge.completed')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12048">
            <div className="text-2xl font-bold text-green-600" data-id="element-12049">
              $
              {completedAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-12050">
            {filteredOrders.filter(o => o.status === 'completed').length}{' '}
            {t('components.ShopDashboard.orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12051">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12052">
            <XCircleIcon size={16} className="text-red-500" data-id="element-12053" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12054">
              {t('components.getOrderStatusBadge.cancelled')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12055">
            <div className="text-2xl font-bold text-red-600" data-id="element-12056">
              $
              {cancelledAmount.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}
            </div>
          </div>
          <div className="px-4 py-2 bg-red-50 text-xs text-red-700 font-medium" data-id="element-12057">
            {filteredOrders.filter(o => o.status === 'cancelled').length}{' '}
            {t('components.ShopDashboard.orders')}
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-12058">
        {/* Tabs + Controls */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b border-gray-200 gap-4" data-id="element-12059">
          <div className="flex space-x-6 overflow-x-auto pb-1" role="tablist" aria-label="Order status tabs" data-id="element-12060">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'new',
            label: 'New'
          }, {
            id: 'in-progress',
            label: 'In Progress'
          }, {
            id: 'open',
            label: 'Open'
          }, {
            id: 'completed',
            label: 'Completed'
          }, {
            id: 'cancelled',
            label: 'Cancelled'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls="orders-tabpanel" id={`orders-tab-${tab.id}`} onClick={() => setActiveTab(tab.id as OrderStatus | 'all')} className={`pb-3 -mb-px text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12061">
                {tab.label}
              </button>)}
          </div>

          <div className="flex items-center gap-3 pb-3" data-id="element-12062">
            <div className="relative" data-id="element-12063">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12064" />

              <input type="search" aria-label={t('pages.Orders.attrs.search_orders')} placeholder={t('pages.Orders.attrs.search_orders')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12065" />
            </div>
            <div className="relative" data-id="element-12066">
              <StoreIcon size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-12067" />
              <select value={shopFilter} onChange={e => setShopFilter(e.target.value)} className="appearance-none pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors cursor-pointer text-gray-700 font-medium" data-id="element-12068">
                <option value="all" data-id="element-12069">{t('pages.Orders.all_shops')}</option>
                {shopsList.map(shop => <option key={shop.id} value={shop.id} data-id="element-12070">
                    {shop.name}
                  </option>)}
              </select>
              <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-12071" />
            </div>{' '}
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-12072">
            <p className="text-gray-500 text-sm" data-id="element-12073">
              {t('pages.Orders.no_orders_found_matching_your_criteria')}
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12074">
            <div className="overflow-x-auto" data-id="element-12075">
              <table className="w-full text-left border-collapse" data-id="element-12076">
                <thead data-id="element-12077">
                  <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-12078">
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12079">
                      {t('pages.Orders.order')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12080">
                      {t('pages.Orders.date')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12081">
                      {t('pages.Orders.customer')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12082">
                      {t('pages.Orders.shop')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12083">
                      {t('components.CampaignList.status')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12084">
                      {t('pages.Orders.payment')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12085">
                      {t('pages.OrderDetail.total')}
                    </th>
                    <th className="px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-12086">
                      {t('pages.CampaignDetails.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-12087">
                  {filteredOrders.map(order => <tr key={order.id} onClick={() => navigate(`/order-detail/${order.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-12088">
                      <td className="px-5 py-4" data-id="element-12089">
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-12090">
                          #{order.id}
                        </div>
                        {order.transaction && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[120px]" data-id="element-12091">
                            {order.transaction}
                          </div>}
                      </td>
                      <td className="px-5 py-4" data-id="element-12092">
                        <div className="flex items-center text-sm text-gray-900" data-id="element-12093">
                          <CalendarIcon size={14} className="mr-1.5 text-gray-400" data-id="element-12094" />

                          {order.date}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-0.5" data-id="element-12095">
                          <ClockIcon size={14} className="mr-1.5 text-gray-400" data-id="element-12096" />

                          {order.time}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-12097">
                        <div className="text-sm font-medium text-gray-900" data-id="element-12098">
                          {order.customer.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[150px]" data-id="element-12099">
                          {order.customer.email}
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-12100">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-12101">
                          <StoreIcon size={11} className="mr-1.5" data-id="element-12102" />
                          {order.shopName}
                        </span>
                      </td>
                      <td className="px-5 py-4" data-id="element-12103">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-5 py-4" data-id="element-12104">
                        <div className="flex items-center" data-id="element-12105">
                          {getPaymentStatusIcon(order.paymentStatus)}
                          <span className="ml-1.5 text-sm text-gray-900 capitalize" data-id="element-12106">
                            {order.paymentStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4" data-id="element-12107">
                        <div className="text-sm font-medium text-gray-900" data-id="element-12108">
                          {formatCurrencyAmount(order.total, {
                      fractionDigits: 2
                    })}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right relative" data-id="element-12109">
                        <div ref={contextMenuOpenId === order.id ? contextMenuRef : null} data-id="element-12110">
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(contextMenuOpenId === order.id ? null : order.id);
                    }} aria-label="Order actions" data-id="element-12111">
                            <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-12112" />
                          </button>

                          {contextMenuOpenId === order.id && <>
                              <div className="fixed inset-0 z-10" onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} data-id="element-12113" />

                              <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12114">
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                          navigate(`/order-detail/${order.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12115">
                                  <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-12116" />

                                  {t('pages.Orders.view_details')}
                                </button>
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12117">
                                  <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-12118" />

                                  {t('pages.Orders.email_customer')}
                                </button>
                                <div className="border-t border-gray-100 my-1" data-id="element-12119" />
                                <button onClick={e => {
                          e.stopPropagation();
                          setContextMenuOpenId(null);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-12120">
                                  <ArchiveIcon size={16} className="mr-3" data-id="element-12121" />
                                  {t('pages.Orders.archive_order')}
                                </button>
                              </div>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}